import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { Box, CircularProgress, FormControl, FormControlLabel, Radio, RadioGroup, Stack, Typography } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { Asset } from "../../data/Asset";
import { SelectVillage } from "../../components/SelectVillage";
import { Dashboard } from "../../enums/Dashboard";
import { useVillageWifiHttp } from "./hooks/useVillageWifiHttp";
import { VillageIssuedKeysDto } from "./dtos/VillageIssuedKeysDto";
import { BarChart, CartesianGrid, XAxis, YAxis, Legend, Bar, Tooltip } from "recharts";


type DataRow = {
    cardholder: number;
    hour: number;
    visitor: number; 
};


export const VillageIssuedKeysStats: React.FC = ( ) => {

    const [ asset, setAsset ] = useState< Asset >( Asset.Waio );
    const [ dateOn, setDateOn ] = useState< string  >( );
    const [ isLoaded, setIsLoaded ] = useState< boolean >( false );
    const [ isLoading, setIsLoading ] = useState< boolean >( false );
    const [ rows, setRows ] = useState< DataRow[ ] >( [ ] );
    const [ village, setVillage ] = useState< string >( '' );

    const { getVillageIssuedKeys } = useVillageWifiHttp( );


    useEffect( () => {

        if( !dateOn || !village ){

            return;

        }

        const fetchData = async() => {
            
            setIsLoading( true );
            setIsLoaded( false );

            const villageIssuedKeys = await getVillageIssuedKeys( dateOn, village );
            
            setIsLoading( false );
            setIsLoaded( true );
            
            if( !villageIssuedKeys ){

                setRows( [] );
                return;

            }
            prepareChartData( villageIssuedKeys );

        };

        fetchData( );

    }, [ dateOn, village ]);


    const handleAssetChange = ( event: React.ChangeEvent< HTMLInputElement > ) => { 
        
        setVillage( '' );
        setAsset( ( event.target as HTMLInputElement ).value as Asset ); 

    };

    const handleDateOnChange = ( date: Dayjs | null ) => {

        if( !date ){
            
            return;

        }

        const day = date.date( ) < 10 ? `0${ date.date( ) }` : `${ date.date( ) }`;
        const month = date.month( ) + 1 < 10 ? `0${ date.month( ) + 1 }` : `${ date.month( ) + 1 }`;
        const year = date.year( );
        const formattedDate = `${ year }-${ month }-${ day }`;
        setDateOn( formattedDate ); 
        
    };

    const handleSelectVillage = ( villageCode: string ) => setVillage( villageCode );


    const prepareChartData = ( villageIssuedKeys: VillageIssuedKeysDto[ ] ) => {

        const rows: DataRow[ ] = [ ];

        for( let hour = 0; hour < 24; ++hour  ){

            const cardholderKeyCount = villageIssuedKeys.find( x => x.issuedHour === hour && x.keyIssuedTo === 'Card Holder' )?.keyCount;
            const visitorKeyCount = villageIssuedKeys.find( x => x.issuedHour === hour && x.keyIssuedTo === 'Visitor' )?.keyCount;

            /*Math.floor(Math.random() * 6) + 1*/

            rows.push({ 
                cardholder: cardholderKeyCount ? cardholderKeyCount : 0,
                hour,
                visitor: visitorKeyCount ? visitorKeyCount : 0,
            });

        }

        setRows( rows );

    };


    return(
        
        <>

            <LocalizationProvider dateAdapter={ AdapterDayjs } adapterLocale="en-au">
                    
                <Stack direction="row" sx={{ marginTop: '10px' }}>

                    <FormControl 
                        sx={{ marginRight: '10px' }}
                        disabled={ isLoading }
                    >
                        {/* <FormLabel>Asset</FormLabel> */}
                        <RadioGroup 
                            value={ asset }
                            onChange={ handleAssetChange }
                        >
                            
                            <FormControlLabel value={ Asset.Waio } control={<Radio />} label={ Asset.Waio } sx={{ marginBottom: '-5px', marginTop: '-5px' }}/>
                            <FormControlLabel value={ Asset.Niw } control={<Radio />} label={ Asset.Niw } sx={{ marginBottom: '-5px', marginTop: '-5px' }}/>

                        </RadioGroup>
                    </FormControl>
                    
                    <SelectVillage asset={ asset } dashboard={ Dashboard.VillageWiFi } onSelectVillage={ handleSelectVillage } village={ village }/>

                    <DatePicker 
                        sx={{ marginLeft: '10px' }}
                        label="Date" 
                        value = { dateOn ? dayjs( dateOn ) : null }
                        onChange={ handleDateOnChange }
                    />


                </Stack>
                    
            </LocalizationProvider>

            {

                isLoading
                    &&
                <Box 
                    display="flex" 
                    alignItems="center"
                    justifyContent="center"
                    marginTop={ 50 }
                >
                    <CircularProgress size="80px" sx={{color: '#e65400'}}/>
                </Box>

                }

            {

                ( isLoaded && rows.length === 0 ) && <Typography sx={{ marginTop: '15px' }}>No issued keys stats to display</Typography>

            }

            {

                rows.length > 0
                    &&
                <BarChart
                    width={ 1000 }
                    height={ 600 } 
                    data={ rows }
                    margin={{
                      top: 5,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" />
                    <Tooltip />
                    <YAxis type="number" domain={['dataMin', 'dataMax + 1']}/>
                    <Legend formatter={ label => label.charAt(0).toUpperCase() + label.slice(1) }/>
                    <Bar dataKey="cardholder" fill="#156082" label={ "Cardholder" }/>
                    <Bar dataKey="visitor" fill="#e97132" label={ "Visitor" }/>
                </BarChart>


            }

        </>
        
    );

};